var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Skeleton, useTheme, useMediaQuery, TablePagination, } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { CardRow, SkeletonRow } from "./CardRow";
import { SButton } from "../buttons";
export const PrimaryGrid = (_a) => {
    var _b, _c, _d, _e;
    var { title, paginationModel, isFullPage, autoHeight = false, onPaginationModelChange, heightVariant, gridHeight, fillHeight = true, renderCard, renderRow, renderSkeletonRow, rowsPrefix, minContentWidth = 0 } = _a, dataGridProps = __rest(_a, ["title", "paginationModel", "isFullPage", "autoHeight", "onPaginationModelChange", "heightVariant", "gridHeight", "fillHeight", "renderCard", "renderRow", "renderSkeletonRow", "rowsPrefix", "minContentWidth"]);
    const { t } = useTranslation("common");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const showCardView = !!renderCard && useMediaQuery(theme.breakpoints.down("lg"));
    const mobileCardEndSpacerSx = {
        height: {
            xs: "calc(124px + env(safe-area-inset-bottom))",
            sm: "calc(108px + env(safe-area-inset-bottom))",
            md: 84,
        },
    };
    const scrollContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const rows = dataGridProps.rows || [];
    const columns = dataGridProps.columns || [];
    const loading = (_b = dataGridProps.loading) !== null && _b !== void 0 ? _b : false;
    const rowCount = dataGridProps.paginationMode === "server"
        ? ((_c = dataGridProps.rowCount) !== null && _c !== void 0 ? _c : rows.length)
        : rows.length;
    const isServerPagination = dataGridProps.paginationMode === "server";
    const [accumulatedRows, setAccumulatedRows] = useState(rows);
    const page = (_d = paginationModel === null || paginationModel === void 0 ? void 0 : paginationModel.page) !== null && _d !== void 0 ? _d : 0;
    const pageSize = (_e = paginationModel === null || paginationModel === void 0 ? void 0 : paginationModel.pageSize) !== null && _e !== void 0 ? _e : 25;
    const pageSizeOptions = dataGridProps.pageSizeOptions || [
        25, 50,
    ];
    const handlePageChange = (_, newPage) => {
        onPaginationModelChange === null || onPaginationModelChange === void 0 ? void 0 : onPaginationModelChange({ page: newPage, pageSize });
    };
    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        onPaginationModelChange === null || onPaginationModelChange === void 0 ? void 0 : onPaginationModelChange({ page: 0, pageSize: newSize });
    };
    useEffect(() => {
        if (!showCardView || !isServerPagination) {
            setAccumulatedRows(rows);
            return;
        }
        // Keep loaded cards visible while requesting next page.
        if (loading && rows.length === 0) {
            return;
        }
        if (page === 0) {
            setAccumulatedRows(rows);
            return;
        }
        setAccumulatedRows((prev) => {
            const merged = [...prev];
            const ids = new Set(prev.map((item) => item === null || item === void 0 ? void 0 : item.id));
            rows.forEach((row) => {
                if (!ids.has(row === null || row === void 0 ? void 0 : row.id)) {
                    merged.push(row);
                    ids.add(row === null || row === void 0 ? void 0 : row.id);
                }
            });
            return merged;
        });
    }, [showCardView, isServerPagination, loading, rows, page]);
    const cardRows = showCardView && isServerPagination ? accumulatedRows : rows;
    const canLoadMore = showCardView && isServerPagination && cardRows.length < rowCount;
    const shouldShowInitialCardSkeleton = showCardView && loading && page === 0 && cardRows.length === 0;
    const handleLoadMore = () => {
        if (loading || !canLoadMore)
            return;
        onPaginationModelChange === null || onPaginationModelChange === void 0 ? void 0 : onPaginationModelChange({ page: page + 1, pageSize });
    };
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el)
            return;
        const update = () => {
            // clientWidth excludes scrollbar; this matches “available viewport”
            setContainerWidth(el.clientWidth);
        };
        update();
        const ro = new ResizeObserver(() => update());
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, []);
    const DEFAULT_COL_WIDTH = 100;
    const { computedWidthByField, totalMinWidth, totalComputedWidth, isOverflow } = useMemo(() => {
        var _a, _b, _c;
        const spacingToNumber = (val) => {
            const raw = theme.spacing(val);
            if (typeof raw === "number")
                return raw;
            return Number.parseFloat(String(raw));
        };
        // These must match `CardRow` desktop styling:
        // - px: 2  (theme.spacing(2))
        // - gap: 1 (theme.spacing(1))
        // - border: 1px on both sides (approx)
        const rowPaddingX = spacingToNumber(2); // px: 2
        const rowGap = spacingToNumber(1); // gap: 1
        const rowBorderPx = 2;
        const totalRowGaps = Math.max(0, columns.length - 1) * rowGap;
        const minByField = {};
        const baseByField = {};
        const flexByField = {};
        // "floor" width: how wide each column must be, considering flex.
        // - flex columns can't shrink below minWidth
        // - non-flex columns can't shrink below their (width || minWidth)
        let totalFloor = 0;
        let totalFlex = 0;
        let totalFlexMin = 0;
        for (const col of columns) {
            const minW = (_b = (_a = (typeof col.minWidth === "number" ? col.minWidth : undefined)) !== null && _a !== void 0 ? _a : (typeof col.width === "number" ? col.width : undefined)) !== null && _b !== void 0 ? _b : DEFAULT_COL_WIDTH;
            const baseW = (_c = (typeof col.width === "number" ? col.width : undefined)) !== null && _c !== void 0 ? _c : minW;
            const flex = typeof col.flex === "number" ? col.flex : 0;
            minByField[col.field] = minW;
            baseByField[col.field] = baseW;
            flexByField[col.field] = flex;
            if (flex > 0) {
                totalFloor += minW;
                totalFlexMin += minW;
                totalFlex += flex;
            }
            else {
                totalFloor += baseW;
            }
        }
        const available = containerWidth || 0;
        // Adjust available space so that sum(column widths) + gaps + padding <= containerWidth
        const adjustedAvailable = Math.max(0, available - rowBorderPx - 2 * rowPaddingX - totalRowGaps);
        // Small tolerance to avoid 1px overflow from rounding differences.
        const overflow = available > 0 ? totalFloor > adjustedAvailable + 1 : false;
        const widths = {};
        if (available <= 0) {
            // Before measurement: use base widths to avoid flicker.
            for (const col of columns)
                widths[col.field] = baseByField[col.field];
            return {
                computedWidthByField: widths,
                totalMinWidth: totalFloor,
                totalComputedWidth: available,
                isOverflow: false,
            };
        }
        if (overflow) {
            // Overflow case: keep flex columns at minWidth and fixed columns at base width.
            for (const col of columns) {
                const flex = flexByField[col.field];
                widths[col.field] = flex > 0 ? minByField[col.field] : baseByField[col.field];
            }
            return {
                computedWidthByField: widths,
                totalMinWidth: totalFloor,
                totalComputedWidth: rowBorderPx + 2 * rowPaddingX + totalRowGaps + totalFloor,
                isOverflow: true,
            };
        }
        // Non-overflow: distribute remaining space across flex columns.
        const remaining = Math.max(0, adjustedAvailable - totalFloor);
        for (const col of columns) {
            const minW = minByField[col.field];
            const flex = flexByField[col.field];
            if (flex > 0 && totalFlex > 0) {
                widths[col.field] = minW + (remaining * flex) / totalFlex;
            }
            else {
                widths[col.field] = baseByField[col.field];
            }
        }
        const totalComputed = 
        // sum(column widths) == adjustedAvailable here
        rowBorderPx + 2 * rowPaddingX + totalRowGaps + adjustedAvailable;
        return {
            computedWidthByField: widths,
            totalMinWidth: totalFloor,
            totalComputedWidth: totalComputed,
            isOverflow: false,
        };
    }, [columns, containerWidth, theme, isMobile]);
    // A custom row owns its own layout, so only its declared minimum decides when to scroll.
    const scrollsHorizontally = renderRow
        ? containerWidth > 0 && containerWidth < minContentWidth
        : isOverflow;
    const contentWidthPx = renderRow ? minContentWidth : Math.ceil(totalComputedWidth);
    return (React.createElement(Box, { sx: Object.assign({ backgroundColor: "transparent", width: "100%", pt: 1.5 }, (fillHeight &&
            !showCardView && {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
        })) },
        React.createElement(Box, { sx: Object.assign({ backgroundColor: "transparent" }, (fillHeight &&
                !showCardView && {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            })) },
            React.createElement(Box, { ref: scrollContainerRef, sx: Object.assign({}, (showCardView
                    ? { height: "auto", overflowY: "visible", overflowX: "visible" }
                    : gridHeight
                        ? { height: gridHeight, overflowY: "auto", overflowX: "auto" }
                        : { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" })) },
                React.createElement(Box, { sx: {
                        minWidth: "100%",
                        width: showCardView
                            ? "100%"
                            : scrollsHorizontally
                                ? `${contentWidthPx}px`
                                : "100%",
                    } },
                    rowsPrefix,
                    showCardView ? (
                    // Responsive card grid for mobile / tablet
                    shouldShowInitialCardSkeleton ? (React.createElement(Grid, { container: true, spacing: 1.5 }, Array.from({ length: pageSize > 6 ? 6 : pageSize }).map((_, i) => (React.createElement(Grid, { key: i, size: { xs: 12, sm: 6, md: 4 } },
                        React.createElement(Skeleton, { variant: "rounded", height: 160, sx: { borderRadius: 3 } })))))) : cardRows.length > 0 ? (React.createElement(Grid, { container: true, spacing: 1.5 }, cardRows.map((row) => (React.createElement(Grid, { key: row.id, size: { xs: 12, sm: 6, md: 4 } }, renderCard(row)))))) : (React.createElement(Box, { sx: { py: 6 } },
                        React.createElement(CustomNoRowsOverlay, null)))) : (
                    // Desktop row layout
                    React.createElement(React.Fragment, null, loading
                        ? Array.from({ length: pageSize > 5 ? 5 : pageSize }).map((_, i) => renderSkeletonRow ? (React.createElement(React.Fragment, { key: i }, renderSkeletonRow(i))) : (React.createElement(SkeletonRow, { key: i, columns: columns, computedWidthByField: computedWidthByField })))
                        : rows.length > 0
                            ? rows.map((row) => renderRow ? (React.createElement(React.Fragment, { key: row.id }, renderRow(row))) : (React.createElement(CardRow, { key: row.id, row: row, columns: columns, computedWidthByField: computedWidthByField, onClick: dataGridProps.onRowClick, isMobile: isMobile })))
                            : !loading && (React.createElement(Box, { sx: { py: 6 } },
                                React.createElement(CustomNoRowsOverlay, null))))))),
            !showCardView && rowCount > 0 && (React.createElement(TablePagination, { component: "div", count: rowCount, page: page, onPageChange: handlePageChange, rowsPerPage: pageSize, onRowsPerPageChange: handleRowsPerPageChange, rowsPerPageOptions: pageSizeOptions, labelRowsPerPage: t("pagination.rowsPerPage") })),
            canLoadMore && (React.createElement(Box, { sx: { mt: 2, mb: 1, display: "flex", justifyContent: "center" } },
                React.createElement(SButton, { variant: "outlined", size: "small", onClick: handleLoadMore, disabled: loading }, loading ? "Loading..." : "Load more"))),
            showCardView && React.createElement(Box, { sx: mobileCardEndSpacerSx }))));
};
const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .no-rows-primary": Object.assign({ fill: "#3D4751" }, theme.applyStyles("light", {
        fill: "#AEB8C2",
    })),
    "& .no-rows-secondary": Object.assign({ fill: "#1D2126" }, theme.applyStyles("light", {
        fill: "#E8EAED",
    })),
}));
function CustomNoRowsOverlay() {
    return (React.createElement(StyledGridOverlay, null,
        React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", width: 96, viewBox: "0 0 452 257", "aria-hidden": true, focusable: "false" },
            React.createElement("path", { className: "no-rows-primary", d: "M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z" }),
            React.createElement("path", { className: "no-rows-primary", d: "M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z" }),
            React.createElement("path", { className: "no-rows-primary", d: "M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z" }),
            React.createElement("path", { className: "no-rows-secondary", d: "M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z" })),
        React.createElement(Box, { sx: { mt: 2 } }, "No data")));
}
